import { ToastService } from '@cudirect/shared-ui-library';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfig } from '@app/config/config.type';
import { RuntimeConfigService } from './runtime-config.service';

/**
 * Provides version checking functionality for the application.
 * This service periodically checks if a new version of the application is available by comparing
 * the current version's build info with the version information fetched from a server-side configuration file.
 * If a new version is detected, it prompts the user to update the application.
 * NOTE: If our config continues to grow, we probably dont want to be pulling down the entire config here... just build.
 */
@Injectable({
  providedIn: 'root'
})
export class VersioningService {
  // Local store of application configuration settings
  private appConfig: AppConfig;

  // Base URL for version checking, constructed from the current location origin
  private readonly origin = window.location.origin;
  private readonly versionCheckUrl = `${this.origin}/assets/data/config.json`;

  // Tracker for how many times the user has been prompted
  private promptCounter = 0;

  /** Initial delay before starting the version check - after this the interval starts per appconfig value */
  private readonly INITIAL_DELAY_MS = 10000;

  constructor(
    private readonly http: HttpClient,
    private readonly toastService: ToastService,
    private readonly runtimeConfigService: RuntimeConfigService
  ) {}

  /**
   * Initiates a periodic check for application version updates.
   * Uses the application configuration to determine if version checking is enabled and at what interval it should occur.
   * NOTE: This is meant to be called once at the root of the application
   */
  public startVersionCheck(): void {
    // Fetch application configuration settings
    this.appConfig = this.runtimeConfigService.getConfig;

    /**  Determine if version checking is enabled based on the application configuration */
    if (!this.versionCheckingEnabled()) return;

    // Lets not start for 5 seconds to give the app time to settle
    setTimeout(() => {
      // After that, start the version check. Assumption is the interval will be much longer than 5 seconds (likely minutes)
      this.checkVersion();

      // Set up a periodic check at the interval specified in the application configuration
      setInterval(() => this.checkVersion(), +this.appConfig.versionCheckMs); // Convert the string interval to a number
    }, this.INITIAL_DELAY_MS);
  }

  /**
   * Fetches the server's app configuration to compare versions and prompts an update if different.
   * Utilizes a cache-busting URL to ensure the latest config is retrieved. Logs errors if the fetch fails.
   */
  public checkVersion() {
    if (!this.isOnline()) return; // Check if the browser is online before making the HTTP request

    // Construct a cache-busting URL for fetching the latest configuration
    const bustParam = new Date().toISOString();
    const cacheBustedUrl = `${this.versionCheckUrl}?cb=check-${bustParam}`;

    // Fetch the latest application configuration from the server
    this.http.get<AppConfig>(cacheBustedUrl).subscribe({
      next: (data) => {
        // Compare the build information from the server with the local configuration
        const localBuildInfo = this.appConfig.buildInfo;

        // If the server has a different build info, warn the user about the available update
        if (data.buildInfo !== localBuildInfo) {
          // Prompt the user to update the application
          this.promptUserToUpdate();
        }
      },
      error: (error) => {
        // Log any errors encountered during the fetch operation
        console.error('Failed to fetch version:', error);
      }
    });
  }

  /**
   * Checks if the browser is currently online.
   * Utilizes the navigator.onLine property to determine the internet connection status.
   * This method also checks if the document is visible, indicating that the user is actively viewing the page,
   * Which covers when a specific tab is minified or hidden in the background, but the overall browser still has a connection.
   *
   * @returns `true` if the browser is online, otherwise returns `false`.
   * @remarks this is broken out for modularity in regard to testing, as well as supporting a future enhancement to catch sleeping tabs
   */
  public isOnline(): boolean {
    return navigator.onLine && document.visibilityState === 'visible';
  }

  /**
   * Prompts the user to update the application by reloading the page.
   * NOTE: We will enhance this asap. This is just a placeholder for now...
   */
  private promptUserToUpdate() {
    // Increment the prompt counter
    // We might want to use this to show more aggressive messages or prompts after a certain point (e.g., switch to confirm?)
    // No need to reset the prompt counter here, since Angular will be nuked on window.reload()
    this.promptCounter++;

    // If we're in admin, hit them over the head with the update dialog
    if (window.location.pathname.includes('admin')) {
      if (confirm('A new version of the application is available. Would you like to update now?')) {
        // Reload the current page to update the application if the user confirms
        window.location.reload();
      }
    } else {
      // Else, something more subtle...
      const TOAST_BAR_TEXT = 'A new version of the application is available. To ensure functionality, please reload the page.';
      const TOAST_BTN_TEXT = 'Reload Page';
      const TOAST_TIMEOUT = null; // Indefinite Toast - see shared-ui-library\src\lib\components\toast\README.md
      const getBldsHTML = (html) => `<span class="bl-font-color--white">${html}</span>`;

      this.toastService
        .open(getBldsHTML(TOAST_BAR_TEXT), TOAST_TIMEOUT, getBldsHTML(TOAST_BTN_TEXT))
        .subscribe(() => window.location.reload());
    }
  }

  /**
   * Determines if version checking is enabled based on the application configuration.
   * Checks for necessary configuration elements and logs a specific warning for each missing or invalid condition.
   * This method primarily ensures that the version checking process only proceeds when all configuration requirements are met.
   * @remarks This method is verbose - but we really need to know if it's on when it's on, and off when it's off (and why).
   * @returns boolean - Returns `true` if version checking is enabled and all conditions are satisfied, otherwise returns `false`.
   */
  private versionCheckingEnabled(): boolean {
    if (!this.appConfig) {
      return false;
    }

    if (!this.appConfig.buildInfo) {
      return false;
    }

    if (!this.appConfig.versionCheckMs) {
      return false;
    }

    const versionCheckMsToNum = Number(this.appConfig.versionCheckMs);
    if (Number.isNaN(versionCheckMsToNum)) {
      return false;
    }

    if (versionCheckMsToNum <= 0) {
      return false;
    }
    return true;
  }
}
